import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { isNumber, isString } from 'lodash-es';
import { Subscription } from 'rxjs';
import assert from 'tiny-invariant';

import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FocusableMixin } from '@ts/shared/form/focus/util-core';
import {
  AutofocusIonicFieldInputProps,
  FormFocusIonInputService,
} from '@ts/shared/form/focus/util-ionic';
import {
  Currency,
  CURRENCY_DEFAULT,
  Money,
  MoneyFormatService,
  MoneyOperationService,
} from '@ts/shared/money/util-core';
import {
  NumberInputComponent,
  NumberInputConfig,
} from '@ts/shared/number/feature-input';

interface MoneyFieldProps {
  // default is money
  returnType?: 'number' | 'money';
}

@Component({
  selector: 'ts-money-field-ui',
  templateUrl: './money-field-ui.component.html',
  styleUrls: ['./money-field-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NumberInputComponent],
})
export class MoneyFieldUiComponent
  extends FieldType<
    FieldTypeConfig<MoneyFieldProps & AutofocusIonicFieldInputProps>
  >
  implements OnInit, OnDestroy, AfterViewInit, FocusableMixin
{
  private moneyFormatService = inject(MoneyFormatService);
  private moneyOperationService = inject(MoneyOperationService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private formFocusIonInputService = inject(FormFocusIonInputService);

  @ViewChild(NumberInputComponent) numberInputComponent!: NumberInputComponent;

  currency: Currency = CURRENCY_DEFAULT;

  currencyDisplay!: string;
  moneyAsNumber: number | null = null;

  subscriptions: Subscription[] = [];
  config: NumberInputConfig = {};

  ngOnInit() {
    this.config = {
      required: this.props.required,
      disabled: this.props.disabled,
      readonly: this.props.readonly,
    };

    let initialValue: Money | number | null;

    if (isString(this.formControl.value)) {
      initialValue = Number(this.formControl.value);

      if (isNaN(initialValue)) {
        initialValue = null;
      }
    } else if (
      this.moneyOperationService.isMoney(this.formControl.value) ||
      isNumber(this.formControl.value)
    ) {
      initialValue = this.formControl.value;
    } else {
      initialValue = null;
    }

    let formValue: Money | number | null;
    if (initialValue === null) {
      this.moneyAsNumber = null;
      formValue = null;
    } else if (isNumber(initialValue)) {
      this.moneyAsNumber = initialValue;

      formValue =
        this.props.returnType !== 'number'
          ? this.moneyOperationService.numberToMoney(
              initialValue,
              this.currency,
            )
          : initialValue;
    } else {
      this.moneyAsNumber =
        this.moneyOperationService.moneyToNumber(initialValue);

      formValue =
        this.props.returnType === 'number' ? this.moneyAsNumber : initialValue;
    }
    this.formControl.setValue(formValue, { emitEvent: false });

    this.currencyDisplay =
      this.moneyFormatService.CURRENCY_TO_DISPLAY[this.currency];

    this.subscriptions.push(
      this.formControl.valueChanges.subscribe(
        (value: Money | number | null | undefined) => {
          if (value === null || value === undefined) {
            this.moneyAsNumber = null;
            this.changeDetectorRef.detectChanges();
            return;
          }

          if (this.props.returnType === 'number') {
            assert(isNumber(value));

            this.moneyAsNumber = value;
          } else {
            assert(this.moneyOperationService.isMoney(value));

            this.moneyAsNumber =
              this.moneyOperationService.moneyToNumber(value);
          }

          this.changeDetectorRef.detectChanges();
        },
      ),
    );
  }

  ngAfterViewInit() {
    this.formFocusIonInputService.setAutofocusIfInProps({
      props: this.props,
      ionInput: this.numberInputComponent.ionInput,
      formControl: this.formControl,
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  setFocus() {
    this.formFocusIonInputService.focus(this.numberInputComponent.ionInput);
  }

  inputChanged(value: number | null) {
    if (value === null) {
      this.formControl.setValue(null);
      return;
    }

    if (this.props.returnType === 'number') {
      this.formControl.setValue(value);
    } else {
      const money = this.moneyOperationService.numberToMoney(
        value,
        this.currency,
      );
      this.formControl.setValue(money);
    }
  }
}
